import type {
  ContractVersionDetailView,
  ContractVersionRole,
  ContractVersionRoleView,
  ContractVersionSource,
  ContractVersionView,
} from "@closer/shared";
import {
  CONTRACT_VERSION_INTEGRITY_NAMES,
  CONTRACT_VERSION_ROLE_NAMES,
  CONTRACT_VERSION_ROLE_PERMISSIONS,
  CONTRACT_VERSION_SOURCE_NAMES,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  FormField,
  Modal,
  Select,
  Skeleton,
  Table,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { uploadFile } from "../../lib/fileUpload";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { ContractVersionAudit } from "./ContractVersionAudit";
import { CounterpartyRevisionDialog } from "./CounterpartyRevisionDialog";

/**
 * VERSİYON GEÇMİŞİ ve versiyon detayı (CLOS-135 `[090]`).
 *
 * ### ⚠ Bu ekran içerik TESLİM ETMİYOR
 *
 * Kart: *"içerik veya dosya teslimi ayrı güvenli görüntüleme/indirme sözleşmesi üzerinden
 * yapılır; liste endpoint'i GİZLİ DEPOLAMA ADRESİ döndürmez."* Burada yalnız metadata var:
 * kaçıncı versiyon, kimden, ne zaman, hangi kaynaktan ve bütünlüğü ne durumda.
 *
 * ### ⚠ Erişim kaybı ÖNBELLEKTEN gizlenmiyor
 *
 * Kart: *"erişim sonradan kaybedilmişse ÖNBELLEKTEKİ veya eski route verisi gösterilmez."*
 * Sorgu tenant anahtarlı (`tenantKey`) ve hata durumunda liste hiç çizilmiyor — eski veriyi
 * ekranda bırakan bir "arka planda yenile" davranışı burada yanlış olurdu.
 */

const LIMIT = 20;

function kaynakAdi(kaynak: ContractVersionSource): string {
  return CONTRACT_VERSION_SOURCE_NAMES[kaynak] ?? kaynak;
}

export function ContractVersionsPage() {
  const navigate = useNavigate();
  const [solVersiyon, setSolVersiyon] = useState("");
  const [sagVersiyon, setSagVersiyon] = useState("");
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  /** Onay bekleyen kaynak versiyon; `null` ise diyalog kapalı. */
  const [kopyaKaynagi, setKopyaKaynagi] = useState<ContractVersionView | null>(null);
  const [revizyonAcik, setRevizyonAcik] = useState(false);
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? "1") || 1;

  const detayYolu = `/app/sozlesmeler/${id}`;

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "versiyonlar", page),
    queryFn: async () =>
      api<ContractVersionView[]>(`/api/contracts/${id}/versions?page=${page}&limit=${LIMIT}`, {
        tenantId,
      }),
  });

  /**
   * ⚠ Roller AYRI sorgu (CLOS-136 `[091]`). Versiyon satırına gömülü gelselerdi, rol
   * değişikliği versiyon kaydını değiştiriyormuş gibi görünürdü — oysa rol versiyona yalnız
   * referans veren ayrı bir işaret.
   */
  const rolSorgusu = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "versiyon-rolleri"),
    queryFn: async () =>
      (await api<ContractVersionRoleView[]>(`/api/contracts/${id}/version-roles`, { tenantId }))
        .data,
  });

  const rolHaritasi = new Map<string, ContractVersionRole[]>();
  for (const r of rolSorgusu.data ?? []) {
    rolHaritasi.set(r.versionId, [...(rolHaritasi.get(r.versionId) ?? []), r.role]);
  }

  /**
   * Geçmiş versiyondan yeni çalışma versiyonu (CLOS-628 — `[097]`'nin arayüz yarısı).
   *
   * ⚠ İstek YALNIZ `sourceVersionId` taşıyor. Şema `version_copy` dalında `baseVersionId`
   * kabul etmiyor: baz zaten kaynağın kendisi ve iki alan taşımak, ikisinin farklı olduğu
   * anlamsız bir kayıt üretilebilmesi demekti.
   */
  const kopyala = useMutation({
    mutationFn: async (kaynak: ContractVersionView) =>
      (
        await api<{ id: string }>(`/api/contracts/${id}/versions`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ source: "version_copy", sourceVersionId: kaynak.id }),
        })
      ).data,
    onSuccess: async (yeni) => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "versiyonlar", page),
      });
      setKopyaKaynagi(null);
      toast.success("Yeni çalışma versiyonu oluşturuldu");
      void navigate(`/app/sozlesmeler/${id}/versiyonlar/${yeni.id}`);
    },
    onError: (error) => {
      setKopyaKaynagi(null);
      toast.error(
        error instanceof ApiRequestError ? error.message : "Yeni çalışma versiyonu oluşturulamadı",
      );
    },
  });

  /**
   * Karşı taraf revizyonu (CLOS-137 `[092]`).
   *
   * ⚠ Dosya ÖNCE yükleniyor, sonra versiyon kaydı isteniyor: baytlar API'den geçmiyor
   * (ADR-0010 D2) ve `uploadFile` taramanın bitmesini de bekliyor — taranmamış bir dosya
   * versiyona bağlanamaz.
   */
  const revizyonYukle = useMutation({
    mutationFn: async (girdi: {
      sourcePartyId: string;
      baseVersionId: string | null;
      dosya: File;
    }) => {
      const yuklenen = await uploadFile({
        file: girdi.dosya,
        domain: "contract",
        entityId: id,
        tenantId,
      });
      return (
        await api(`/api/contracts/${id}/counterparty-revisions`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({
            storageObjectId: yuklenen.storageObjectId,
            sourcePartyId: girdi.sourcePartyId,
            baseVersionId: girdi.baseVersionId,
          }),
        })
      ).data;
    },
    onSuccess: async () => {
      setRevizyonAcik(false);
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contract", id) });
      toast.success("Karşı taraf revizyonu kaydedildi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Revizyon yüklenemedi");
    },
  });

  const columns: ColumnDef<ContractVersionView>[] = [
    {
      accessorKey: "versionNumber",
      header: "Sürüm",
      /**
       * ⚠ GÜNCEL İŞARETİ (CLOS-1279 · matris 16).
       *
       * Liste hiçbir satırı güncel diye işaretlemiyordu: v3'e bakan kullanıcı v5'in var
       * olduğunu ancak satır sayarak anlıyordu. Belge §5.6 bayat versiyonla çalışan
       * kullanıcının güncele yönlendirilmesini istiyor; ilk adım hangisinin güncel
       * olduğunu SÖYLEMEK.
       *
       * ⚠ Bu bir KAPI değil BİLGİ: resmî işlemler bilerek versiyona bağlı (değişmezlik),
       * eski versiyonla çalışmak yasak değil.
       *
       * ⚠ En büyük numara SAYFA İÇİNDEN değil, LİSTENİN tamamından hesaplanamaz —
       * sayfalama var. Bu yüzden ölçüt `page === 1` ve sıralama azalan: ilk sayfanın en
       * üst satırı gerçekten en güncel versiyondur. Sonraki sayfalarda rozet çizilmiyor,
       * çünkü orada güncel versiyon zaten yok.
       */
      cell: ({ row }) => (
        <span className="flex items-center gap-2">
          <Link
            className="font-medium text-primary hover:underline"
            to={`/app/sozlesmeler/${id}/versiyonlar/${row.original.id}`}
          >
            v{row.original.versionNumber}
          </Link>
          {page === 1 && row.index === 0 ? <Badge tone="success">Güncel</Badge> : null}
        </span>
      ),
    },
    {
      accessorKey: "sourceType",
      header: "Kaynak",
      cell: ({ row }) => kaynakAdi(row.original.sourceType),
    },
    {
      accessorKey: "baseVersionNumber",
      header: "Baz",
      /** Baz yoksa BOŞ değil açık bir işaret: boş hücre "yüklenmedi mi?" sorusunu doğurur. */
      cell: ({ row }) =>
        row.original.baseVersionNumber ? `v${row.original.baseVersionNumber}` : "—",
    },
    { accessorKey: "createdByName", header: "Oluşturan" },
    {
      accessorKey: "createdAt",
      header: "Tarih",
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleString("tr-TR"),
    },
    {
      id: "roller",
      header: "Rol",
      cell: ({ row }) => {
        const roller = rolHaritasi.get(row.original.id) ?? [];
        if (roller.length === 0) return "—";
        return (
          <span className="flex flex-wrap gap-1">
            {roller.map((rol) => (
              <Badge key={rol} tone="success">
                {CONTRACT_VERSION_ROLE_NAMES[rol]}
              </Badge>
            ))}
          </span>
        );
      },
    },
    {
      accessorKey: "sourceAvailable",
      header: "İçerik",
      cell: ({ row }) =>
        row.original.sourceAvailable ? (
          <span className="text-sm text-fg-muted">{row.original.fileName ?? "Uygulama içi"}</span>
        ) : (
          /** ⚠ Kaynak kaldırılmışsa SESSİZ kalmıyor: satır duruyor, durumu söyleniyor. */
          <Badge tone="warning">Kaynak erişilemiyor</Badge>
        ),
    },
  ];

  /**
   * Eylem sütunu YALNIZ yetkiliye çiziliyor. Sunucu kapısı `contract.edit`; düğmeyi herkese
   * gösterip 403 duvarına toslatmak kullanıcıyı yöneticisine boşuna gönderirdi.
   */
  if (can("contract.edit")) {
    columns.push({
      id: "kopya",
      header: "Eylem",
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          /**
           * ⚠ Kaynağı kaldırılmış versiyon kopyalanamıyor: sunucu bunu `content_purged` ile
           * zaten reddediyor. Açık bırakmak kullanıcıyı boş bir tura sokardı.
           */
          disabled={!row.original.sourceAvailable}
          onClick={() => setKopyaKaynagi(row.original)}
        >
          Yeni çalışma versiyonu
        </Button>
      ),
    });
  }

  if (sorgu.isError) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
        <Link className="text-sm font-medium text-primary hover:underline" to={detayYolu}>
          Sözleşmeye dön
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-bold text-fg-heading sm:text-3xl">Versiyon geçmişi</h1>
        {/*
          KARŞI TARAF REVİZYONU (CLOS-137 `[092]`). Yeri burası: gelen belge geçmişe yeni
          bir değişmez versiyon olarak giriyor, sözleşmenin metnini değiştirmiyor.
        */}
        {can("contract.revision") ? (
          <Button type="button" size="sm" variant="secondary" onClick={() => setRevizyonAcik(true)}>
            Karşı taraf revizyonu yükle
          </Button>
        ) : null}
      </div>

      <CounterpartyRevisionDialog
        open={revizyonAcik}
        onClose={() => setRevizyonAcik(false)}
        contractId={id}
        versiyonlar={sorgu.data?.data ?? []}
        onConfirm={(girdi) => revizyonYukle.mutate(girdi)}
        isPending={revizyonYukle.isPending}
      />

      {/*
        KARŞILAŞTIRMA GİRİŞİ (CLOS-141 `[096]`). Kart, bu ekranı *"kullanıcıya kaynak
        versiyonları seçtiren UI girişi"* sayıyor. Seçim burada yapılıyor, karşılaştırmayı
        redline ekranı üretiyor — bu sayfa sonuç hakkında hiçbir şey bilmiyor.

        ⚠ Aynı versiyon iki kez seçilemiyor: sunucu bunu zaten `same_version` ile reddediyor
        ve kullanıcıyı boş bir tura sokmanın anlamı yok.
      */}
      {(sorgu.data?.data.length ?? 0) >= 2 ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            navigate(`/app/sozlesmeler/${id}/karsilastir?left=${solVersiyon}&right=${sagVersiyon}`);
          }}
        >
          <FormField label="Sol versiyon">
            <Select value={solVersiyon} onChange={(e) => setSolVersiyon(e.target.value)}>
              <option value="">Seçin</option>
              {(sorgu.data?.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>{`Versiyon ${v.versionNumber}`}</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Sağ versiyon">
            <Select value={sagVersiyon} onChange={(e) => setSagVersiyon(e.target.value)}>
              <option value="">Seçin</option>
              {(sorgu.data?.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>{`Versiyon ${v.versionNumber}`}</option>
              ))}
            </Select>
          </FormField>
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            disabled={!solVersiyon || !sagVersiyon || solVersiyon === sagVersiyon}
          >
            Karşılaştır
          </Button>
        </form>
      ) : null}

      {sorgu.isPending ? (
        <div role="status" aria-label="Versiyonlar yükleniyor">
          <Skeleton lines={5} />
        </div>
      ) : (
        <Table
          columns={columns}
          data={sorgu.data?.data ?? []}
          pagination={{
            page,
            limit: LIMIT,
            total: sorgu.data?.meta?.total ?? 0,
            onPageChange: (p) => {
              const sonraki = new URLSearchParams(params);
              sonraki.set("page", String(p));
              setParams(sonraki);
            },
          }}
          empty="Bu sözleşmenin henüz versiyonu yok."
        />
      )}

      <Link className="text-sm font-medium text-primary hover:underline" to={detayYolu}>
        Sözleşmeye dön
      </Link>
      {/*
        ONAY ADIMI: yeni versiyon oluşturmak tarihsel bir kayıt yazar ve geri alınmaz.
        Hangi versiyondan türetildiği kullanıcıya AÇIKÇA söyleniyor; tek tıkla yapılsaydı
        kullanıcı yanlış satırdan kopyaladığını ancak sonuçta fark ederdi.
      */}
      <Modal
        open={kopyaKaynagi !== null}
        onClose={() => setKopyaKaynagi(null)}
        title="Yeni çalışma versiyonu"
        footer={
          <>
            <Button variant="secondary" onClick={() => setKopyaKaynagi(null)}>
              Vazgeç
            </Button>
            <Button
              onClick={() => kopyaKaynagi && kopyala.mutate(kopyaKaynagi)}
              loading={kopyala.isPending}
            >
              Oluştur
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg-muted">
          {`Versiyon ${kopyaKaynagi?.versionNumber ?? ""} içeriğinden yeni bir çalışma versiyonu oluşturulacak. Kaynak versiyon değişmez.`}
        </p>
      </Modal>
    </div>
  );
}

/**
 * Tek versiyonun DETAYI.
 *
 * ⚠ Doğrudan URL ile açılıyor olması bir yetki değil: uç aynı yetki ve kayıt erişimi
 * kapılarından geçiyor ve versiyon SÖZLEŞMEYE bağlanarak aranıyor.
 */
export function ContractVersionDetailPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "", versionId = "" } = useParams();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "versiyon", versionId),
    queryFn: async () =>
      (
        await api<ContractVersionDetailView>(`/api/contracts/${id}/versions/${versionId}`, {
          tenantId,
        })
      ).data,
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Versiyon yükleniyor">
        <Skeleton lines={6} />
      </div>
    );
  }

  if (sorgu.error || !sorgu.data) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          state={resolveErrorState(sorgu.error, {
            idempotent: true,
            overrides: {
              forbiddenOrMissing: {
                title: "Versiyon bulunamadı",
                description: "Bu versiyon yok ya da erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void sorgu.refetch()}
        />
        <Link
          className="text-sm font-medium text-primary hover:underline"
          to={`/app/sozlesmeler/${id}/versiyonlar`}
        >
          Versiyon geçmişine dön
        </Link>
      </div>
    );
  }

  const v = sorgu.data;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-bold text-fg-heading sm:text-3xl">
          Versiyon v{v.versionNumber}
        </h1>
        {/*
          ⚠ Bütünlük durumu AÇIKÇA gösteriliyor. "Doğrulandı" dışındaki her durum uyarı tonu:
          sessiz bir gösterim, bozulmuş bir versiyonu sağlam sanmaya yol açardı.
        */}
        <Badge tone={v.integrity === "verified" ? "success" : "warning"}>
          {CONTRACT_VERSION_INTEGRITY_NAMES[v.integrity]}
        </Badge>
      </header>

      <Card>
        <dl className="flex flex-col gap-1">
          <Satir etiket="Kaynak" deger={kaynakAdi(v.sourceType)} />
          <Satir etiket="Oluşturan" deger={v.createdByName} />
          <Satir etiket="Tarih" deger={new Date(v.createdAt).toLocaleString("tr-TR")} />
          <Satir
            etiket="Baz versiyon"
            deger={v.baseVersionNumber ? `v${v.baseVersionNumber}` : "—"}
          />
          <Satir etiket="Dosya" deger={v.fileName ?? "—"} />
          <Satir
            etiket="Madde sayısı"
            deger={v.clauseCount === null ? "—" : String(v.clauseCount)}
          />
          {/* Özet KISALTILMADAN gösteriliyor: doğrulama yapan kişi tamamını karşılaştırmalı. */}
          <Satir etiket="Bütünlük özeti" deger={<code className="text-xs">{v.contentHash}</code>} />
          <Satir
            etiket="İçerik durumu"
            deger={v.sourceAvailable ? "Erişilebilir" : "Kaynak erişilemiyor"}
          />
        </dl>
      </Card>

      {/*
        Belge ekranı (CLOS-120 `[101]`) yalnız DOSYAYA dayanan versiyonlarda anlamlı:
        uygulama içi metin versiyonunun indirilecek kaynak dosyası yok, bağlantı da
        kullanıcıyı boş bir ekrana götürürdü.
      */}
      {v.fileName !== null && (
        <Link
          className="text-sm font-medium text-primary hover:underline"
          to={`/app/sozlesmeler/${id}/versiyonlar/${versionId}/belge`}
        >
          Belgeyi görüntüle ve indir
        </Link>
      )}

      <RolAtama contractId={id} versionId={versionId} />

      {/* Versiyon geçmişi (CLOS-143 `[102]`) — yetkisi olmayan için hiç çizilmiyor. */}
      <ContractVersionAudit contractId={id} versionId={versionId} />

      <Link
        className="text-sm font-medium text-primary hover:underline"
        to={`/app/sozlesmeler/${id}/versiyonlar`}
      >
        Versiyon geçmişine dön
      </Link>
    </div>
  );
}

const ROL_YOLLARI: Record<ContractVersionRole, string> = {
  current_working: "current",
  shared_with_counterparty: "shared",
  approved: "approved",
};

/**
 * ROL ATAMA (CLOS-136 `[091]`).
 *
 * ⚠ Düğmeler `can()` ile gizleniyor ama karar SUNUCUDA: her rolün kendi yetkisi var
 * (`contract.edit` / `contract.share` / `contract.approve`) ve komut onu yeniden uyguluyor.
 * Görünürlük bir kolaylık; yetki değil.
 *
 * ⚠ İstek BEKLENEN ÖNCEKİ değeri taşıyor: başkasının az önce işaretlediği versiyonu
 * sessizce ezmek, "hangi metin onaylıydı?" sorusunu cevapsız bırakırdı.
 */
function RolAtama({ contractId, versionId }: { contractId: string; versionId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const roller = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "versiyon-rolleri"),
    queryFn: async () =>
      (
        await api<ContractVersionRoleView[]>(`/api/contracts/${contractId}/version-roles`, {
          tenantId,
        })
      ).data,
  });

  const ata = useMutation({
    mutationFn: async (girdi: { role: ContractVersionRole; expectedVersionId: string | null }) =>
      api(`/api/contracts/${contractId}/version-roles/${ROL_YOLLARI[girdi.role]}`, {
        method: "PUT",
        tenantId,
        body: JSON.stringify({ versionId, expectedVersionId: girdi.expectedVersionId }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", contractId),
      });
      toast.success("Versiyon rolü güncellendi");
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Rol güncellenemedi"),
  });

  const mevcut = new Map((roller.data ?? []).map((r) => [r.role, r.versionId]));
  const atanabilir = (Object.keys(ROL_YOLLARI) as ContractVersionRole[]).filter(
    (rol) => can(CONTRACT_VERSION_ROLE_PERMISSIONS[rol]) && mevcut.get(rol) !== versionId,
  );
  if (atanabilir.length === 0) return null;

  return (
    <div role="group" aria-label="Versiyon rolleri" className="flex flex-wrap gap-2">
      {atanabilir.map((rol) => (
        <Button
          key={rol}
          type="button"
          variant="secondary"
          size="sm"
          loading={ata.isPending}
          onClick={() => ata.mutate({ role: rol, expectedVersionId: mevcut.get(rol) ?? null })}
        >
          {CONTRACT_VERSION_ROLE_NAMES[rol]} yap
        </Button>
      ))}
    </div>
  );
}

function Satir({ etiket, deger }: { etiket: string; deger: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border-default py-1.5 last:border-0">
      <dt className="text-sm text-fg-muted">{etiket}</dt>
      <dd className="text-right text-sm text-fg">{deger}</dd>
    </div>
  );
}
